import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { billsService } from '../../services/payables/bills';
import { vendorsService } from '../../services/payables/vendors';
import { useAuth } from '../../contexts/AuthContext';

const EditBillModal = ({ bill, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    billNumber: '',
    vendorId: '',
    billDate: '',
    dueDate: '',
    description: '',
    subtotal: 0,
    taxAmount: 0,
    totalAmount: 0,
    paymentStatus: 'pending',
    status: 'active',
    lineItems: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && bill) {
      loadVendors();
      // Pre-populate form with bill data
      setFormData({
        billNumber: bill.billNumber || '',
        vendorId: bill.vendorId || '',
        billDate: bill.billDate?.toDate?.() ? bill.billDate.toDate().toISOString().split('T')[0] : '',
        dueDate: bill.dueDate?.toDate?.() ? bill.dueDate.toDate().toISOString().split('T')[0] : '',
        description: bill.description || '',
        subtotal: bill.subtotal || 0,
        taxAmount: bill.taxAmount || 0,
        totalAmount: bill.totalAmount || 0,
        paymentStatus: bill.paymentStatus || 'pending',
        status: bill.status || 'active',
        lineItems: bill.lineItems || []
      });
      setErrors({});
    }
  }, [isOpen, bill]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVendors = async () => {
    try {
      const vendorsList = await vendorsService.getVendors(user.companyId);
      setVendors(vendorsList);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedLineItems = [...formData.lineItems];
    updatedLineItems[index] = {
      ...updatedLineItems[index],
      [field]: value
    };
    
    // Recalculate line total
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = parseFloat(updatedLineItems[index].quantity) || 0;
      const unitPrice = parseFloat(updatedLineItems[index].unitPrice) || 0;
      updatedLineItems[index].total = quantity * unitPrice;
    }
    
    setFormData(prev => ({
      ...prev,
      lineItems: updatedLineItems
    }));
    
    // Recalculate totals
    calculateTotals(updatedLineItems);
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, {
        description: '',
        quantity: 1,
        unitPrice: '',
        total: 0
      }]
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = formData.lineItems.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      lineItems: updatedLineItems
    }));
    calculateTotals(updatedLineItems);
  };

  const calculateTotals = (lineItems) => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = subtotal * 0.15; // 15% tax
    const totalAmount = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      taxAmount,
      totalAmount
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.billNumber.trim()) {
      newErrors.billNumber = t('common.billNumber') + ' ' + t('bill_payments.required');
    }
    
    if (!formData.vendorId) {
      newErrors.vendorId = t('bills.vendor') + ' ' + t('bill_payments.required');
    }
    
    if (!formData.billDate) {
      newErrors.billDate = t('bill_payments.billDate') + ' ' + t('bill_payments.required');
    }
    
    if (!formData.dueDate) {
      newErrors.dueDate = t('bill_payments.dueDate') + ' ' + t('bill_payments.required');
    }
    
    if (formData.lineItems.length === 0) {
      newErrors.lineItems = t('bill_modals.atLeastOneLineItem') + ' ' + t('bill_payments.required');
    }
    
    // Validate line items
    formData.lineItems.forEach((item, index) => {
      if (!item.description.trim()) {
        newErrors[`lineItem_${index}_description`] = t('accounting.description') + ' ' + t('bill_payments.required');
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`lineItem_${index}_quantity`] = t('bill_modals.validQuantity') + ' ' + t('bill_payments.required');
      }
      if (!item.unitPrice || item.unitPrice <= 0) {
        newErrors[`lineItem_${index}_unitPrice`] = t('bill_modals.validUnitPrice') + ' ' + t('bill_payments.required');
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const billData = {
        ...formData,
        billDate: new Date(formData.billDate),
        dueDate: new Date(formData.dueDate),
        companyId: user.companyId,
        updatedBy: user.uid
      };
      
      await billsService.updateBill(bill.id, billData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating bill:', error);
      setErrors({ submit: t('bill_modals.errorUpdatingBill') });
    } finally {
      setLoading(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{t('bill_modals.editBill')}</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.billNumber')} *
              </label>
              <input
                type="text"
                name="billNumber"
                value={formData.billNumber}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.billNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('bill_modals.enterBillNumber')}
              />
              {errors.billNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.billNumber}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bills.vendor')} *
              </label>
              <select
                name="vendorId"
                value={formData.vendorId}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.vendorId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('bill_modals.selectVendor')}</option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </select>
              {errors.vendorId && (
                <p className="mt-1 text-sm text-red-600">{errors.vendorId}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.billDate')} *
              </label>
              <input
                type="date"
                name="billDate"
                value={formData.billDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.billDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.billDate && (
                <p className="mt-1 text-sm text-red-600">{errors.billDate}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.dueDate')} *
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.dueDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('clients.paymentStatus')}
              </label>
              <select
                name="paymentStatus"
                value={formData.paymentStatus}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">{t('accounting.pending')}</option>
                <option value="partial">{t('accounting.partial')}</option>
                <option value="paid">{t('accounting.filter.paid')}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('accounting.table.status')}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">{t('clients.active')}</option>
                <option value="cancelled">{t('accounting.cancelled')}</option>
              </select>
            </div>
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('accounting.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('bill_payments.enterDescription')}
            />
          </div>

          {/* Line Items */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('bill_modals.lineItems')}</h3>
              <button
                type="button"
                onClick={addLineItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {t('bill_modals.addLineItem')}
              </button>
            </div>
            
            {errors.lineItems && (
              <p className="mb-4 text-sm text-red-600">{errors.lineItems}</p>
            )}
            
            <div className="space-y-4">
              {formData.lineItems.map((item, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('accounting.description')}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors[`lineItem_${index}_description`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('bill_payments.enterDescription')}
                      />
                      {errors[`lineItem_${index}_description`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`lineItem_${index}_description`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('accounting.quantity')}
                      </label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors[`lineItem_${index}_quantity`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        min="0"
                        step="0.01"
                      />
                      {errors[`lineItem_${index}_quantity`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`lineItem_${index}_quantity`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.unitPrice')}
                      </label>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleLineItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors[`lineItem_${index}_unitPrice`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        min="0"
                        step="0.01"
                      />
                      {errors[`lineItem_${index}_unitPrice`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`lineItem_${index}_unitPrice`]}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="text-sm font-medium text-gray-700">
                        {t('accounting.total')}: {item.total.toFixed(2)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        className="text-red-600 hover:text-red-700 focus:outline-none"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('accounting.subtotal')}:</span>
                <span>{formData.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>{t('bills.tax')} (15%):</span>
                <span>{formData.taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold border-t pt-2">
                <span>{t('accounting.total')}:</span>
                <span>{formData.totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="text-red-600 text-sm">{errors.submit}</div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('bill_payments.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t('bill_modals.updating') : t('bill_modals.updateBill')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBillModal; 